/**
 * useDashboardLayout — persisted state engine behind DashboardGridWrapper.
 *
 * Persistence: localStorage `user_dashboard_layout_[ROLE]_[VIEW_ID]` (instant)
 * + debounced sync to `PUT /api/preferences/dashboard-layout/{viewId}`.
 * Backend copy (if any) wins over localStorage on mount so layouts roam
 * across devices.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { GRID_COLS, GRID_MAX_H, nextY, reconcileState, storageKeyFor } from "@/lib/layout-types";

function dispatchResizeSoon() {
    // Recharts / lightweight-charts recompute on window resize.
    setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
}

export function useDashboardLayout({ role, viewId, widgets }) {
    const storageKey = storageKeyFor(role, viewId);
    const widgetsRef = useRef(widgets);
    widgetsRef.current = widgets;
    const widgetKey = widgets.map((w) => w.id).join(",");

    const [state, setState] = useState(() => {
        try {
            const raw = JSON.parse(window.localStorage.getItem(storageKey) || "null");
            return reconcileState(raw, widgets);
        } catch {
            return reconcileState(null, widgets);
        }
    });

    // Re-reconcile when the widget catalog itself changes (product rollout).
    const widgetKeyRef = useRef(widgetKey);
    useEffect(() => {
        if (widgetKeyRef.current === widgetKey) return;
        widgetKeyRef.current = widgetKey;
        setState((prev) => reconcileState({
            layouts: prev.layouts, minimized: prev.minimized,
            hidden_widget_ids: prev.hidden, saved_h: prev.savedH,
        }, widgetsRef.current));
    }, [widgetKey]);

    // Backend hydrate — server copy wins if present (no echo PUT afterwards).
    const skipNextPut = useRef(false);
    useEffect(() => {
        let alive = true;
        api.get(`/preferences/dashboard-layout/${viewId}`)
            .then(({ data }) => {
                if (alive && data?.layouts && Object.keys(data.layouts).length) {
                    skipNextPut.current = true;
                    setState(reconcileState(data, widgetsRef.current));
                }
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [viewId]);

    // Persist: localStorage instantly, backend debounced 1.2s.
    const firstRun = useRef(true);
    useEffect(() => {
        const payload = {
            role,
            layouts: state.layouts,
            minimized: state.minimized,
            hidden_widget_ids: state.hidden,
            saved_h: state.savedH,
        };
        try { window.localStorage.setItem(storageKey, JSON.stringify(payload)); } catch {}
        if (firstRun.current) { firstRun.current = false; return; }
        if (skipNextPut.current) { skipNextPut.current = false; return; }
        const t = setTimeout(() => {
            api.put(`/preferences/dashboard-layout/${viewId}`, payload).catch(() => {});
        }, 1200);
        return () => clearTimeout(t);
    }, [state, role, viewId, storageKey]);

    const onLayoutChange = useCallback((_current, all) => {
        setState((prev) => {
            const merged = { ...prev.layouts };
            for (const bp of Object.keys(all || {})) {
                merged[bp] = all[bp].map((it) => ({
                    i: it.i, x: it.x, y: it.y, w: it.w, h: it.h,
                    minW: it.minW, minH: it.minH, maxH: it.maxH || GRID_MAX_H, isResizable: it.isResizable,
                }));
            }
            if (JSON.stringify(merged) === JSON.stringify(prev.layouts)) return prev;
            return { ...prev, layouts: merged };
        });
    }, []);

    const toggleMinimize = useCallback((id) => {
        setState((prev) => {
            const isMin = prev.minimized.includes(id);
            const wdef = widgetsRef.current.find((w) => w.id === id);
            const savedH = { ...prev.savedH };
            if (!isMin) savedH[id] = {};
            const layouts = {};
            for (const bp of Object.keys(prev.layouts)) {
                layouts[bp] = prev.layouts[bp].map((it) => {
                    if (it.i !== id) return it;
                    if (!isMin) {
                        savedH[id][bp] = it.h;
                        return { ...it, h: 1, minH: 1, isResizable: false };
                    }
                    const restoreH = prev.savedH?.[id]?.[bp] || wdef?.layout?.h || 4;
                    return { ...it, h: restoreH, minH: wdef?.minH || 2, isResizable: true };
                });
            }
            return {
                ...prev, layouts, savedH,
                minimized: isMin ? prev.minimized.filter((x) => x !== id) : [...prev.minimized, id],
            };
        });
        dispatchResizeSoon();
    }, []);

    const hideWidget = useCallback((id) => {
        setState((prev) => {
            const layouts = {};
            for (const bp of Object.keys(prev.layouts)) {
                layouts[bp] = prev.layouts[bp].filter((it) => it.i !== id);
            }
            return {
                ...prev, layouts,
                hidden: [...new Set([...prev.hidden, id])],
                minimized: prev.minimized.filter((x) => x !== id),
            };
        });
    }, []);

    // Restoration: y = Math.max(...currentLayout.map(i => i.y + i.h)) — no overlap.
    const restoreWidget = useCallback((id) => {
        setState((prev) => {
            if (!prev.hidden.includes(id)) return prev;
            const wdef = widgetsRef.current.find((w) => w.id === id);
            const hidden = prev.hidden.filter((x) => x !== id);
            if (!wdef) return { ...prev, hidden };
            const layouts = { ...prev.layouts };
            for (const bp of Object.keys(GRID_COLS)) {
                const cols = GRID_COLS[bp];
                const cur = layouts[bp] || [];
                const width = Math.min(wdef.layout.w, cols);
                layouts[bp] = [...cur, {
                    i: id, x: 0, y: nextY(cur), w: width, h: wdef.layout.h,
                    minW: Math.min(wdef.minW || 2, cols), minH: wdef.minH || 2, maxH: GRID_MAX_H,
                }];
            }
            return { ...prev, layouts, hidden };
        });
        dispatchResizeSoon();
    }, []);

    const restoreAll = useCallback(() => {
        setState((prev) => {
            let next = prev;
            for (const id of prev.hidden) {
                const wdef = widgetsRef.current.find((w) => w.id === id);
                if (!wdef) continue;
                const layouts = { ...next.layouts };
                for (const bp of Object.keys(GRID_COLS)) {
                    const cols = GRID_COLS[bp];
                    const cur = layouts[bp] || [];
                    const width = Math.min(wdef.layout.w, cols);
                    layouts[bp] = [...cur, {
                        i: id, x: 0, y: nextY(cur), w: width, h: wdef.layout.h,
                        minW: Math.min(wdef.minW || 2, cols), minH: wdef.minH || 2, maxH: GRID_MAX_H,
                    }];
                }
                next = { ...next, layouts };
            }
            return { ...next, hidden: [] };
        });
        dispatchResizeSoon();
    }, []);

    const resetLayout = useCallback(() => {
        setState(reconcileState(null, widgetsRef.current));
        try { window.localStorage.removeItem(storageKey); } catch {}
        api.delete(`/preferences/dashboard-layout/${viewId}`).catch(() => {});
        dispatchResizeSoon();
    }, [storageKey, viewId]);

    return {
        layouts: state.layouts,
        minimized: state.minimized,
        hidden: state.hidden,
        onLayoutChange,
        toggleMinimize,
        hideWidget,
        restoreWidget,
        restoreAll,
        resetLayout,
        dispatchResizeSoon,
    };
}
